import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Badge,
  Card,
  CardContent,
  DialogTitle,
  Dialog,
  DialogContent,
  IconButton,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMainStore, IItem } from '../stores/mainStore';
import { Trans } from './Trans';
import CloseIcon from '@mui/icons-material/Close';

export const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: 'transparent',
    background: 'linear-gradient(to top, #fde047, #f97316)',
    color: 'white',
    fontSize: '1.2rem',
    height: '80px',
    minWidth: '80px',
    padding: '0',
    transform: 'translate(50%, 50%)',
    clipPath:
      'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', // Star shape
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const TierCard = styled(Card)(() => ({
  maxWidth: 600,
  margin: '0 auto',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  transition: 'transform 0.2s ease-in-out',
  //   '&:hover': {
  //     transform: 'translateY(-4px)',
  //   },
}));

const patreonLink = `https://www.patreon.com/join/LewdSpace`;

const subscribeStarLink = `https://subscribestar.adult/lewdspace/subscribe`;

const getTierPayments = (
  user: string | undefined,
  tier: number,
  activeCharacter: IItem | undefined,
  setOpenModal: (open: boolean) => void
) => [
  {
    name: 'Credit/Debit Card',
    onClick: !activeCharacter?.mainEmailVerified
      ? () => setOpenModal(true)
      : undefined,
    url: `${import.meta.env.VITE_HOST}/card-payment/${tier}?userId=${user}`,
    icon: '/public/cardsLogo2.png',
    color: '#FFC700',
    size: 'full',
    active: activeCharacter?.ccbillAvailable,
  },
  {
    name: 'Patreon',
    url: patreonLink,
    icon: '/public/patreonLogo.png',
    color: '#FF424D',
    active: activeCharacter?.patreonAvailable,
  },
  {
    name: 'SubscribeStar',
    url: subscribeStarLink,
    icon: '/public/subscribeStarSmall.png',
    color: '#0088CC',
    active: activeCharacter?.subscribeStarAvailable,
  },
];

export const Prices: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();
  const activeCharacter = useMainStore((state) => state.activeCharacter);
  const user = useMainStore((state) => state.user);
  const setLoginModalOpen = useMainStore((state) => state.setLoginModalOpen);
  const { lang } = useParams<{ lang: string }>();
  return (
    <div className="flex flex-col gap-6">
      {[
        {
          img: '/public/tier1.jpg',
          title: 'BASIC PASS',
          emoji: '💦',
          price: '5',
          features: [
            'Unlock exclusive characters',
            '5 custom messages per day (write your own text in chat)',
          ],
          payments: getTierPayments(user, 1, activeCharacter, setOpenModal),
        },
        {
          img: '/public/tier2.jpg',
          title: 'VIP PASS',
          emoji: '👑',
          price: '8',
          features: [
            'Unlock exclusive characters',
            '25 custom messages per day (write your own text in chat)',
            '5 poses to choose from every turn (during sex scene)',
          ],
          payments: getTierPayments(user, 2, activeCharacter, setOpenModal),
        },
        {
          img: '/public/tier3.jpg',
          title: 'GOD MODE',
          emoji: '⚡',
          price: '14',
          features: [
            'Unlock exclusive characters',
            'Unlimited custom messages (write your own text in chat)',
            'Master of seduction💕 - start a sex scene whenever you want',
            'Stamina king💦 - you decide when to cum',
            'The Undeniable - send messages even to characters who blocked you',
          ],
          payments: getTierPayments(user, 3, activeCharacter, setOpenModal),
        },
      ].map((tier, index) => (
        <TierCard key={index} elevation={3}>
          <CardContent sx={{ p: 0 }}>
            <div className="relative">
              <img
                src={tier.img}
                alt={tier.title}
                className="w-full aspect-[16/9] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {tier.title}{' '}
                  <span style={{ fontSize: '1.5em' }}>{tier.emoji}</span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                  }}
                >
                  <span style={{ fontSize: '1.5em' }}>${tier.price}</span>
                  <span style={{ fontSize: '0.8em', opacity: 0.8 }}>
                    /month
                  </span>
                </Typography>
              </div>
            </div>
            <div className="p-6">
              <div className="flex flex-col gap-3 items-start w-full">
                {tier.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3 w-full">
                    <div className="w-1.5 h-1.5 rounded-full bg-pink-500 flex-shrink-0 mt-2" />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.primary',
                        lineHeight: 1.4,
                        textAlign: 'left',
                        flex: 1,
                      }}
                    >
                      {feature}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
            {/* Add payment options */}
            <div className="pt-0">
              <div className="flex flex-wrap gap-3">
                {tier.payments.map((payment, payIdx) =>
                  payment.active ? (
                    <a
                      key={payIdx}
                      href={payment.onClick ? undefined : payment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        flex: payment.size === 'full' ? '1 1 100%' : '1 1 0',
                        minWidth: payment.size === 'full' ? '100%' : '120px',
                      }}
                    >
                      <button
                        style={{
                          padding: '8px 20px',
                          borderRadius: '12px',
                          background: 'transparent',
                          color: payment.color,
                          fontWeight: 'bold',
                          border: `2px solid ${payment.color}`,
                          cursor: 'pointer',
                          fontSize: '1rem',
                          transition: 'all 0.2s',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          payment.onClick?.();
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = payment.color;
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = payment.color;
                        }}
                      >
                        <img
                          src={payment.icon}
                          alt={payment.name}
                          style={{
                            height: '26px',
                            objectFit: 'contain',
                          }}
                        />
                        {payment.name}
                      </button>
                    </a>
                  ) : null
                )}
              </div>
            </div>
          </CardContent>
        </TierCard>
      ))}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background:
              'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', color: '#1f2937' }}
          >
            <Trans>Login Required</Trans>
          </Typography>
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <div className="text-center">
            <div className="mb-4">
              <Typography
                variant="body1"
                sx={{
                  color: '#4b5563',
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                }}
              >
                <Trans>
                  You need to be logged in with a verified email to subscribe
                  with credit/debit card
                </Trans>
              </Typography>
            </div>
            <div className="flex flex-col gap-3 mt-6">
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  setOpenModal(false);
                  setLoginModalOpen(true);
                }}
                sx={{
                  background:
                    'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  fontWeight: 'bold',
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.5)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Trans>Login to Continue</Trans>
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setOpenModal(false);
                  navigate(`/${lang}/signup`);
                }}
                sx={{
                  color: '#6b7280',
                  borderColor: '#d1d5db',
                  fontWeight: 'bold',
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: 'rgba(107, 114, 128, 0.05)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Trans>Create New Account</Trans>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
